/**
 * Export utilities — Excel y PDF
 */
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatDate } from './dateUtils';

// Logo cache (base64 data URL) and natural aspect ratio
let _logoDataUrl = null;
let _logoLoading = null;
let _logoAspect = 3.469; // default basado en el logo actual (1776×512); se ajusta al cargar

/**
 * Load the AVIPORK logo from /avipork-logo.png and convert to base64 dataURL.
 * Cached after first load. Returns null if loading fails (PDF will fall back to text-only header).
 */
export function loadLogoDataUrl() {
  if (_logoDataUrl) return Promise.resolve(_logoDataUrl);
  if (_logoLoading) return _logoLoading;
  _logoLoading = new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const w = img.naturalWidth || 1024;
          const h = img.naturalHeight || 1024;
          if (h > 0) _logoAspect = w / h;
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          _logoDataUrl = canvas.toDataURL('image/png');
          resolve(_logoDataUrl);
        } catch (_e) { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = '/avipork-logo.png';
    } catch (_e) { resolve(null); }
  });
  return _logoLoading;
}

// Trigger logo load early so it's cached when PDFs are generated
if (typeof window !== 'undefined') {
  loadLogoDataUrl();
}

/**
 * Draw the AVIPORK header (green band + logo) on the current PDF page.
 * El logo ya incluye el texto "AVIPORK" y el tagline, así que no se duplica.
 * Returns the Y coordinate where content can start below the header.
 */
function drawAviporkHeader(doc, { compact = false } = {}) {
  const pageW = doc.internal.pageSize.getWidth();
  // Altura del logo (mantiene legibilidad) y ancho calculado por proporción real
  const logoH = compact ? 42 : 56;
  const logoW = logoH * _logoAspect;
  // La banda tiene un poco más de alto que el logo para dar respiración
  const headerH = logoH + (compact ? 18 : 26);
  const logoY = (headerH - logoH) / 2;

  // Banda verde pastel
  doc.setFillColor(198, 229, 177);
  doc.rect(0, 0, pageW, headerH, 'F');

  // Logo completo (incluye texto AVIPORK + tagline embebidos en la imagen)
  if (_logoDataUrl) {
    try {
      doc.addImage(_logoDataUrl, 'PNG', 30, logoY, logoW, logoH);
    } catch (_e) {
      doc.setTextColor(10, 77, 20);
      doc.setFontSize(compact ? 18 : 22);
      doc.setFont('helvetica', 'bold');
      doc.text('AVIPORK', 30, headerH / 2 + 6);
      doc.setTextColor(0);
    }
  } else {
    doc.setTextColor(10, 77, 20);
    doc.setFontSize(compact ? 18 : 22);
    doc.setFont('helvetica', 'bold');
    doc.text('AVIPORK', 30, headerH / 2 + 6);
    doc.setTextColor(0);
  }

  return headerH;
}

/**
 * Export an array of objects to Excel.
 * columns: [{ key: 'numero_factura', label: 'N° Factura' }, ...]
 * filename without extension.
 */
export function exportToExcel(rows, columns, filename = 'reporte', sheetName = 'Reporte') {
  if (!rows || rows.length === 0) {
    return false;
  }
  const data = rows.map((row) => {
    const obj = {};
    columns.forEach((col) => {
      const raw = col.accessor ? col.accessor(row) : row[col.key];
      obj[col.label] = raw === null || raw === undefined ? '' : raw;
    });
    return obj;
  });
  const ws = XLSX.utils.json_to_sheet(data);
  const colWidths = columns.map((col) => ({ wch: Math.max(col.label.length + 2, col.width || 14) }));
  ws['!cols'] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  const stamp = new Date().toISOString().split('T')[0];
  XLSX.writeFile(wb, `${filename}_${stamp}.xlsx`);
  return true;
}

/**
 * Generate a PDF table report.
 */
export function exportTableToPDF(rows, columns, { title = 'Reporte', filename = 'reporte', subtitle } = {}) {
  if (!rows || rows.length === 0) return false;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

  drawAviporkHeader(doc);

  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 40, 104);
  if (subtitle) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(subtitle, 40, 120);
  }
  const generated = `Generado: ${new Date().toLocaleString('es-ES')}`;
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text(generated, 40, subtitle ? 134 : 120);
  doc.setTextColor(0);

  const head = [columns.map((c) => c.label)];
  const body = rows.map((r) => columns.map((c) => {
    const v = c.accessor ? c.accessor(r) : r[c.key];
    return v === null || v === undefined ? '' : String(v);
  }));

  autoTable(doc, {
    head,
    body,
    startY: subtitle ? 146 : 130,
    styles: { font: 'helvetica', fontSize: 9, cellPadding: 4, lineColor: [200, 220, 200] },
    headStyles: { fillColor: [142, 188, 99], textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 252, 245] },
  });

  const stamp = new Date().toISOString().split('T')[0];
  doc.save(`${filename}_${stamp}.pdf`);
  return true;
}

/**
 * Generate a SINGLE invoice as a clean PDF (printable factura).
 * mode:
 *   'save'  -> download .pdf file (default)
 *   'print' -> open in a new tab and trigger print dialog (user can also "Save as PDF" from there)
 * variant:
 *   'cliente' -> versión para entregar al cliente (NO muestra huevos rotos)
 *   'interna' -> versión interna de almacén (muestra huevos rotos para contabilidad)
 */
export function exportFacturaToPDF(factura, cliente = null, mode = 'save', variant = 'cliente', empresa = null) {
  if (!factura) return false;

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();

  // Header (banda verde + logo completo)
  drawAviporkHeader(doc);

  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0);
  doc.text('CONDUCE', pageW - 40, 36, { align: 'right' });
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(factura.numero_factura || '-', pageW - 40, 56, { align: 'right' });
  doc.setFontSize(9);
  doc.setTextColor(80);
  const rncStr = (empresa && empresa.rnc) ? `RNC: ${empresa.rnc}` : '';
  if (rncStr) {
    doc.text(rncStr, pageW - 40, 70, { align: 'right' });
    doc.text(`Fecha: ${formatDate(factura.fecha)}`, pageW - 40, 84, { align: 'right' });
  } else {
    doc.text(`Fecha: ${formatDate(factura.fecha)}`, pageW - 40, 72, { align: 'right' });
  }

  // Etiqueta de variante (Cliente / Original)
  const isInterna = variant === 'interna';
  const variantLabel = isInterna ? 'ORIGINAL' : 'COPIA CLIENTE';
  const variantColor = isInterna ? [40, 80, 30] : [60, 100, 120];
  doc.setFillColor(...variantColor);
  doc.roundedRect(40, 96, 160, 18, 3, 3, 'F');
  doc.setTextColor(255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(variantLabel, 120, 108, { align: 'center' });
  doc.setTextColor(0);

  // Cliente section
  doc.setTextColor(0);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('CLIENTE', 40, 130);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(factura.cliente_nombre || cliente?.nombre || 'N/A', 40, 148);
  if (cliente?.telefono) {
    doc.setFontSize(9);
    doc.setTextColor(80);
    doc.text(`Tel: ${cliente.telefono}`, 40, 162);
  }
  if (cliente?.direccion) {
    doc.setFontSize(9);
    doc.setTextColor(80);
    doc.text(`Dir: ${cliente.direccion}`, 40, 175);
  }
  doc.setTextColor(0);

  // Estado factura
  const estado = factura.estado_pago || 'Pendiente';
  const colorEstado = estado === 'Pagada' ? [76, 145, 65] : (estado === 'Parcial' ? [194, 142, 56] : [200, 70, 70]);
  doc.setFillColor(...colorEstado);
  doc.roundedRect(pageW - 140, 130, 100, 22, 4, 4, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(estado.toUpperCase(), pageW - 90, 145, { align: 'center' });
  doc.setTextColor(0);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  if (factura.fecha_vencimiento) {
    doc.text(`Vence: ${formatDate(factura.fecha_vencimiento)}`, pageW - 40, 168, { align: 'right' });
  }

  // Helpers de formato (separadores de miles con coma)
  const fmtInt = (n) => Number(n || 0).toLocaleString('en-US');
  const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Items table
  const items = (factura.items || []).map((it) => [
    String(it.tipo_huevo || '').toUpperCase(),
    it.lote_codigo ? String(it.lote_codigo) : '—',
    fmtInt(it.cantidad),
    fmtMoney(it.precio_unitario),
    fmtMoney(it.subtotal ?? (it.cantidad * it.precio_unitario)),
  ]);

  autoTable(doc, {
    head: [['Tipo de Huevo', 'Lote', 'Cantidad', 'Precio Unit.', 'Subtotal']],
    body: items,
    startY: 200,
    styles: { font: 'helvetica', fontSize: 10, cellPadding: 6 },
    headStyles: { fillColor: [142, 188, 99], textColor: [255, 255, 255], fontStyle: 'bold' },
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'center' },
      2: { halign: 'right' },
      3: { halign: 'right' },
      4: { halign: 'right' },
    },
    alternateRowStyles: { fillColor: [248, 252, 245] },
  });

  let y = doc.lastAutoTable.finalY + 16;

  // === RESUMEN POR LOTE (solo en Original/Interna) ===
  // Agrupa las salidas (items vendidos + huevos rotos) por lote, para que en
  // almacén se sepa exactamente cuánto sale de cada lote.
  if (isInterna) {
    const porLote = {};
    (factura.items || []).forEach((it) => {
      const code = it.lote_codigo || 'Sin lote';
      const key = String(code);
      if (!porLote[key]) porLote[key] = { lote: key, tipos: {}, total: 0 };
      const t = String(it.tipo_huevo || '').toLowerCase();
      porLote[key].tipos[t] = (porLote[key].tipos[t] || 0) + Number(it.cantidad || 0);
      porLote[key].total += Number(it.cantidad || 0);
    });
    (factura.huevos_rotos || []).forEach((r) => {
      const code = r.lote_codigo || 'Sin lote';
      const key = String(code);
      if (!porLote[key]) porLote[key] = { lote: key, tipos: {}, total: 0 };
      const t = `${String(r.tipo_huevo || '').toLowerCase()} (rotos)`;
      const cant = Number(r.cantidad_rota ?? r.cantidad ?? 0);
      porLote[key].tipos[t] = (porLote[key].tipos[t] || 0) + cant;
      porLote[key].total += cant;
    });
    const gruposLote = Object.values(porLote);
    if (gruposLote.length > 0) {
      // Preferir ordenar por número de lote alfanumérico
      gruposLote.sort((a, b) => String(a.lote).localeCompare(String(b.lote), 'es', { numeric: true }));

      const filasResumen = [];
      gruposLote.forEach((g) => {
        const label = /^lote\b/i.test(g.lote) ? g.lote : (g.lote === 'Sin lote' ? g.lote : `Lote ${g.lote}`);
        const detalle = Object.entries(g.tipos)
          .map(([t, c]) => `${t}: ${fmtInt(c)}`)
          .join('  \u2022  ');
        filasResumen.push([label, detalle, fmtInt(g.total)]);
      });

      autoTable(doc, {
        head: [['SALIDA POR LOTE', 'Detalle por tipo', 'Total unid.']],
        body: filasResumen,
        startY: y,
        styles: { font: 'helvetica', fontSize: 9.5, cellPadding: 5 },
        headStyles: { fillColor: [76, 145, 65], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'left' },
        columnStyles: {
          0: { halign: 'left', fontStyle: 'bold', cellWidth: 110 },
          1: { halign: 'left' },
          2: { halign: 'right', fontStyle: 'bold', cellWidth: 80 },
        },
        alternateRowStyles: { fillColor: [248, 252, 245] },
      });
      y = doc.lastAutoTable.finalY + 16;
    }
  }

  // Huevos rotos (SOLO en variante interna/original) — lista simple sin montos
  const rotos = factura.huevos_rotos || [];
  let totalCantRotos = 0;
  if (isInterna && rotos.length > 0) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text('Huevos rotos del despacho:', 40, y);
    y += 16;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(40);
    rotos.forEach((r) => {
      const cant = Number(r.cantidad_rota ?? r.cantidad ?? 0);
      totalCantRotos += cant;
      const tipoLabel = String(r.tipo_huevo || '').toLowerCase();
      const loteLbl = r.lote_codigo
        ? (/^lote\b/i.test(String(r.lote_codigo)) ? ` \u00b7 ${r.lote_codigo}` : ` \u00b7 Lote ${r.lote_codigo}`)
        : '';
      doc.text(`\u2022  ${tipoLabel}${loteLbl}: ${fmtInt(cant)} unid.`, 56, y);
      y += 14;
    });
    doc.setTextColor(0);
    y += 6;
  }

  // Totals section (right-aligned)
  doc.setDrawColor(200);
  doc.line(pageW - 240, y, pageW - 40, y);
  y += 16;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Total huevos:', pageW - 240, y);
  doc.text(fmtInt(factura.total_huevos), pageW - 40, y, { align: 'right' });
  y += 16;

  if (isInterna && factura.total_rotos > 0) {
    doc.text('Total rotos:', pageW - 240, y);
    doc.text(fmtInt(factura.total_rotos), pageW - 40, y, { align: 'right' });
    y += 16;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('TOTAL:', pageW - 240, y);
  doc.text(fmtMoney(factura.total_monto), pageW - 40, y, { align: 'right' });
  y += 18;

  if (factura.estado_pago === 'Parcial' || factura.estado_pago === 'Pendiente') {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(180, 50, 50);
    doc.text('Saldo pendiente:', pageW - 240, y);
    doc.text(fmtMoney(factura.saldo), pageW - 40, y, { align: 'right' });
    doc.setTextColor(0);
  }

  // Observaciones
  if (factura.observaciones) {
    y += 30;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Observaciones:', 40, y);
    doc.setFont('helvetica', 'normal');
    y += 14;
    const lines = doc.splitTextToSize(factura.observaciones, pageW - 80);
    doc.text(lines, 40, y);
    y += lines.length * 12;
  }

  // ===== FIRMAS: DESPACHADO POR / RECIBIDO POR =====
  const footerY = doc.internal.pageSize.getHeight() - 40;
  // Posición de firmas: cerca del fondo pero con espacio para el footer
  let firmasY = Math.max(y + 60, footerY - 90);
  // Si no cabe, mover hacia arriba (mínimo dejando margen del footer)
  if (firmasY > footerY - 70) firmasY = footerY - 70;

  // Líneas de firma
  const lineY = firmasY;
  const leftLineStart = 60;
  const leftLineEnd = 240;
  const rightLineStart = pageW - 240;
  const rightLineEnd = pageW - 60;

  doc.setDrawColor(80);
  doc.setLineWidth(0.6);
  doc.line(leftLineStart, lineY, leftLineEnd, lineY);
  doc.line(rightLineStart, lineY, rightLineEnd, lineY);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(60);
  const leftCenter = (leftLineStart + leftLineEnd) / 2;
  const rightCenter = (rightLineStart + rightLineEnd) / 2;
  doc.text('DESPACHADO POR', leftCenter, lineY + 14, { align: 'center' });
  doc.text('RECIBIDO POR', rightCenter, lineY + 14, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(120);
  doc.text('Nombre y Firma', leftCenter, lineY + 26, { align: 'center' });
  doc.text('Nombre y Firma', rightCenter, lineY + 26, { align: 'center' });
  doc.setTextColor(0);

  // Footer
  const footY = doc.internal.pageSize.getHeight() - 40;
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text('AVIPORK · Sistema de Gestión Avícola', pageW / 2, footY, { align: 'center' });
  doc.text(`Generado el ${new Date().toLocaleString('es-ES')}`, pageW / 2, footY + 12, { align: 'center' });

  const variantSuffix = isInterna ? '_original' : '_cliente';
  const filename = `Factura_${factura.numero_factura || 'sin-numero'}${variantSuffix}.pdf`;

  if (mode === 'print') {
    // Open in a new tab and auto-trigger print dialog (user can choose "Save as PDF" from there)
    doc.autoPrint();
    const blobUrl = doc.output('bloburl');
    const win = window.open(blobUrl, '_blank');
    if (!win) {
      // Popup blocked: fallback to saving so the user still gets the file
      doc.save(filename);
      return 'saved-fallback';
    }
    return true;
  }

  doc.save(filename);
  return true;
}


/**
 * Generate a weekly inventory count report PDF.
 * conteo: {fecha_conteo, conteos: {tipo: {sistema, fisico, diferencia}}, total_*, observaciones}
 */
export function exportConteoToPDF(conteo, mode = 'save') {
  if (!conteo) return false;

  const TIPO_LABELS = {
    grande: 'Grandes',
    mediano: 'Medianos',
    pequeño: 'Pequeños',
    pequeno: 'Pequeños',
    doble: 'Dobles',
    sucio: 'Sucios',
    casquiado: 'Casquiados',
  };
  const TIPO_ORDER = ['grande', 'mediano', 'pequeño', 'doble', 'sucio', 'casquiado'];

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();

  // Header (logo completo)
  drawAviporkHeader(doc);

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0);
  doc.text('CONTEO DE INVENTARIO', pageW - 40, 36, { align: 'right' });
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Fecha: ${formatDate(conteo.fecha_conteo)}`, pageW - 40, 56, { align: 'right' });
  doc.setFontSize(8);
  doc.setTextColor(80);
  doc.text(`Generado: ${new Date().toLocaleString('es-ES')}`, pageW - 40, 72, { align: 'right' });
  doc.setTextColor(0);

  // Resumen ejecutivo
  const totSis = conteo.total_sistema || 0;
  const totFis = conteo.total_fisico || 0;
  const totDif = conteo.total_diferencia || 0;
  const exactitud = totSis > 0 ? Math.max(0, 100 - (Math.abs(totDif) / totSis) * 100) : (totDif === 0 ? 100 : 0);

  let y = 120;
  doc.setFillColor(245, 250, 240);
  doc.roundedRect(40, y, pageW - 80, 70, 6, 6, 'F');

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(60, 100, 60);
  doc.text('TOTAL SISTEMA', 60, y + 22);
  doc.text('TOTAL FÍSICO', 220, y + 22);
  doc.text('DIFERENCIA', 380, y + 22);
  doc.text('EXACTITUD', pageW - 140, y + 22);

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0);
  doc.text(String(totSis), 60, y + 50);
  doc.text(String(totFis), 220, y + 50);
  // Diferencia color: rojo si negativa, verde si positiva, gris si 0
  const difColor = totDif < 0 ? [180, 50, 50] : (totDif > 0 ? [60, 130, 60] : [100, 100, 100]);
  doc.setTextColor(...difColor);
  const difLabel = totDif > 0 ? `+${totDif}` : String(totDif);
  doc.text(difLabel, 380, y + 50);
  doc.setTextColor(0);
  doc.text(`${exactitud.toFixed(1)}%`, pageW - 140, y + 50);

  y += 90;

  // Tabla detalle por tipo
  const body = TIPO_ORDER.map((tipo) => {
    const c = conteo.conteos?.[tipo] || conteo.conteos?.[tipo === 'pequeño' ? 'pequeno' : tipo] || {};
    const sis = Number(c.sistema || 0);
    const fis = Number(c.fisico || 0);
    const dif = fis - sis;
    let estado = 'OK';
    if (dif < 0) estado = `Faltante (${Math.abs(dif)})`;
    else if (dif > 0) estado = `Sobrante (+${dif})`;
    return [
      TIPO_LABELS[tipo],
      String(sis),
      String(fis),
      dif > 0 ? `+${dif}` : String(dif),
      estado,
    ];
  });

  autoTable(doc, {
    head: [['Tipo de Huevo', 'Sistema', 'Físico', 'Diferencia', 'Estado']],
    body,
    startY: y,
    styles: { font: 'helvetica', fontSize: 10, cellPadding: 8 },
    headStyles: { fillColor: [142, 188, 99], textColor: [255, 255, 255], fontStyle: 'bold' },
    columnStyles: {
      0: { halign: 'left', fontStyle: 'bold' },
      1: { halign: 'right' },
      2: { halign: 'right' },
      3: { halign: 'right', fontStyle: 'bold' },
      4: { halign: 'center' },
    },
    alternateRowStyles: { fillColor: [248, 252, 245] },
    didParseCell: (hookData) => {
      if (hookData.section === 'body' && (hookData.column.index === 3 || hookData.column.index === 4)) {
        const raw = hookData.row.raw[3];
        const num = parseInt(raw, 10);
        if (!isNaN(num)) {
          if (num < 0) hookData.cell.styles.textColor = [180, 50, 50];
          else if (num > 0) hookData.cell.styles.textColor = [60, 130, 60];
        }
      }
    },
    foot: [['TOTALES', String(totSis), String(totFis), totDif > 0 ? `+${totDif}` : String(totDif), exactitud.toFixed(1) + '% exactitud']],
    footStyles: { fillColor: [220, 240, 200], textColor: [40, 80, 30], fontStyle: 'bold' },
  });

  y = doc.lastAutoTable.finalY + 24;

  // Desglose por lote (nuevo): si el conteo tiene `_por_lote` en el JSON,
  // agregamos una tabla adicional con Tipo · Lote · Sistema · Físico · Dif.
  const porLote = Array.isArray(conteo.conteos?._por_lote) ? conteo.conteos._por_lote : [];
  if (porLote.length > 0) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text('DESGLOSE POR LOTE', 40, y);
    y += 8;
    const rowsLote = porLote.map((r) => {
      const tipoLbl = TIPO_LABELS[r.tipo_huevo] || r.tipo_huevo;
      const codeRaw = String(r.lote_codigo || '').trim();
      const loteLbl = codeRaw
        ? (/^lote\b/i.test(codeRaw) ? codeRaw : `Lote ${codeRaw}`)
        : `Lote ${(r.lote_id || '').slice(0, 6)}`;
      const sis = Number(r.sistema || 0);
      const fis = Number(r.fisico || 0);
      const dif = fis - sis;
      const estado = dif < 0 ? `Faltante (${Math.abs(dif)})` : dif > 0 ? `Sobrante (+${dif})` : 'OK';
      return [tipoLbl, loteLbl, String(sis), String(fis), dif > 0 ? `+${dif}` : String(dif), estado];
    });
    autoTable(doc, {
      head: [['Tipo', 'Lote', 'Sistema', 'Físico', 'Diferencia', 'Estado']],
      body: rowsLote,
      startY: y + 4,
      styles: { font: 'helvetica', fontSize: 9, cellPadding: 6 },
      headStyles: { fillColor: [142, 188, 99], textColor: [255, 255, 255], fontStyle: 'bold' },
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'left', fontStyle: 'bold' },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right', fontStyle: 'bold' },
        5: { halign: 'center' },
      },
      alternateRowStyles: { fillColor: [248, 252, 245] },
      didParseCell: (hookData) => {
        if (hookData.section === 'body' && (hookData.column.index === 4 || hookData.column.index === 5)) {
          const raw = hookData.row.raw[4];
          const num = parseInt(raw, 10);
          if (!isNaN(num)) {
            if (num < 0) hookData.cell.styles.textColor = [180, 50, 50];
            else if (num > 0) hookData.cell.styles.textColor = [60, 130, 60];
          }
        }
      },
    });
    y = doc.lastAutoTable.finalY + 20;
  }

  // Observaciones
  if (conteo.observaciones && conteo.observaciones.trim()) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Observaciones:', 40, y);
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const lines = doc.splitTextToSize(conteo.observaciones, pageW - 80);
    doc.text(lines, 40, y);
    y += lines.length * 12 + 10;
  }

  // Sección de firmas
  if (y < doc.internal.pageSize.getHeight() - 100) {
    y = Math.max(y, doc.internal.pageSize.getHeight() - 120);
    doc.setDrawColor(180);
    doc.line(60, y, 240, y);
    doc.line(pageW - 240, y, pageW - 60, y);
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text('Realizado por', 150, y + 14, { align: 'center' });
    doc.text('Supervisado por', pageW - 150, y + 14, { align: 'center' });
  }

  // Footer
  const footY = doc.internal.pageSize.getHeight() - 40;
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text('AVIPORK · Sistema de Gestión Avícola', pageW / 2, footY, { align: 'center' });

  const filename = `Conteo_${(conteo.fecha_conteo || '').slice(0, 10)}.pdf`;

  if (mode === 'print') {
    doc.autoPrint();
    const blobUrl = doc.output('bloburl');
    const win = window.open(blobUrl, '_blank');
    if (!win) {
      doc.save(filename);
      return 'saved-fallback';
    }
    return true;
  }
  doc.save(filename);
  return true;
}


/**
 * Generate the "Análisis de Producción" PDF.
 *
 * @param {Object} payload
 * @param {Object} payload.resumen          KPIs returned by /api/analisis/produccion
 * @param {Array}  payload.serie            Detail rows (filtered)
 * @param {Object} payload.filtros          Applied filters context for the header
 *   - lote_label (string)         e.g., "Todos" or "Lote 12"
 *   - nave_label (string)         e.g., "Todas las naves" or "Nave 6"
 *   - fecha_desde, fecha_hasta    ISO yyyy-mm-dd
 *   - semana_desde, semana_hasta  number | ''
 *   - vista                       'dia' | 'semana_edad'
 * @param {('save'|'print')} [mode='print']
 */
export function exportAnalisisProduccionToPDF({ resumen, serie, filtros }, mode = 'print') {
  if (!serie || serie.length === 0) return false;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();

  // Helpers
  const fmtInt = (n) => Number(n || 0).toLocaleString('en-US');
  const fmtDec = (n, d = 2) =>
    Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });

  // ===== HEADER (logo completo + banda verde) =====
  drawAviporkHeader(doc);

  doc.setTextColor(0);
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.text('REPORTE DE ANÁLISIS DE PRODUCCIÓN', pageW - 40, 36, { align: 'right' });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80);
  doc.text(`Generado: ${new Date().toLocaleString('es-ES')}`, pageW - 40, 56, { align: 'right' });
  doc.setTextColor(0);

  // ===== APPLIED FILTERS BAR =====
  let y = 104;
  doc.setFillColor(245, 250, 240);
  doc.roundedRect(40, y, pageW - 80, 48, 5, 5, 'F');

  const f = filtros || {};
  const desdeStr = f.fecha_desde ? formatDate(f.fecha_desde) : 'Todos';
  const hastaStr = f.fecha_hasta ? formatDate(f.fecha_hasta) : 'Hoy';
  const semanasStr =
    (f.semana_desde !== '' && f.semana_desde !== null && f.semana_desde !== undefined) ||
    (f.semana_hasta !== '' && f.semana_hasta !== null && f.semana_hasta !== undefined)
      ? `${f.semana_desde !== '' && f.semana_desde !== null && f.semana_desde !== undefined ? f.semana_desde : '0'} a ${f.semana_hasta !== '' && f.semana_hasta !== null && f.semana_hasta !== undefined ? f.semana_hasta : 'sin tope'}`
      : 'Todas';
  const vistaStr = f.vista === 'semana_edad' ? 'Por semana de edad' : 'Por día';

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(60, 100, 60);

  // Row 1: Lote, Nave, Vista
  doc.text('LOTE:', 56, y + 16);
  doc.text('NAVE:', 240, y + 16);
  doc.text('VISTA:', 470, y + 16);
  doc.text('PERÍODO:', 56, y + 36);
  doc.text('SEMANAS DE EDAD:', 380, y + 36);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0);
  doc.text(String(f.lote_label || 'Todos'), 90, y + 16);
  doc.text(String(f.nave_label || 'Todas'), 280, y + 16);
  doc.text(vistaStr, 510, y + 16);
  doc.text(`${desdeStr}  -  ${hastaStr}`, 110, y + 36);
  doc.text(semanasStr, 480, y + 36);

  y += 64;

  // ===== KPI CARDS =====
  if (resumen) {
    const cardW = (pageW - 80 - 30) / 4; // 4 cards with 10pt gap
    const kpis = [
      {
        label: 'POSTURA PROMEDIO',
        value: `${(resumen.postura_promedio_pct || 0).toFixed(1)}%`,
        sub: `${fmtInt(resumen.total_huevos)} huevos · ${resumen.dias_periodo || 0} días`,
        color: [22, 163, 74],
      },
      {
        label: 'MORTALIDAD ACUMULADA',
        value: `${(resumen.mortalidad_acumulada_pct || 0).toFixed(2)}%`,
        sub: `${fmtInt(resumen.total_mortalidad)} de ${fmtInt(resumen.gallinas_iniciales)} aves`,
        color: [220, 38, 38],
      },
      {
        label: 'ALIMENTO CONSUMIDO',
        value: `${(resumen.alimento_qq || 0).toFixed(2)} qq`,
        sub: `${fmtInt(resumen.alimento_kg)} kg`,
        color: [217, 119, 6],
      },
      {
        label: 'POBLACIÓN ACTIVA',
        value: fmtInt(resumen.gallinas_activas_actual),
        sub: `${resumen.num_naves || 0} nave(s) · ${resumen.num_registros || 0} registros`,
        color: [40, 80, 30],
      },
    ];

    kpis.forEach((k, i) => {
      const x = 40 + i * (cardW + 10);
      // Card border
      doc.setDrawColor(220);
      doc.setFillColor(252, 253, 250);
      doc.roundedRect(x, y, cardW, 64, 4, 4, 'FD');
      // Left color stripe
      doc.setFillColor(...k.color);
      doc.rect(x, y, 4, 64, 'F');
      // Texts
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(120);
      doc.text(k.label, x + 12, y + 16);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...k.color);
      doc.text(k.value, x + 12, y + 38);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(120);
      doc.text(k.sub, x + 12, y + 54);
    });

    doc.setTextColor(0);
    y += 80;
  }

  // ===== DETAIL TABLE =====
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0);
  doc.text(`Detalle por registro (${serie.length})`, 40, y);
  y += 6;

  const head = [[
    'Fecha', 'Lote', 'Nave', 'Sem. Edad',
    'Huevos', 'Gallinas', '% Postura',
    'Mort.', '% Mort. Acum.', 'Alimento (qq)',
  ]];

  const body = serie.map((r) => [
    formatDate(r.fecha),
    r.lote_nombre || '—',
    r.nave_nombre || '—',
    r.semana_edad !== null && r.semana_edad !== undefined ? `Sem ${r.semana_edad}` : '—',
    fmtInt(r.total_huevos),
    fmtInt(r.gallinas_activas),
    `${Number(r.postura_pct || 0).toFixed(1)}%`,
    fmtInt(r.mortalidad),
    `${Number(r.mortalidad_acumulada_pct || 0).toFixed(2)}%`,
    `${fmtDec(r.alimento_qq, 2)} qq`,
  ]);

  // Totals foot row
  const totHuevos = serie.reduce((s, r) => s + (r.total_huevos || 0), 0);
  const totMort = serie.reduce((s, r) => s + (r.mortalidad || 0), 0);
  const totAli = serie.reduce((s, r) => s + (r.alimento_qq || 0), 0);
  const avgPost = serie.length
    ? serie.reduce((s, r) => s + (r.postura_pct || 0), 0) / serie.length
    : 0;
  const foot = [[
    'TOTALES', '', '', '',
    fmtInt(totHuevos), '',
    `${avgPost.toFixed(1)}% prom.`,
    fmtInt(totMort), '',
    `${fmtDec(totAli, 2)} qq`,
  ]];

  autoTable(doc, {
    head,
    body,
    foot,
    startY: y + 6,
    styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 4, lineColor: [220, 230, 220] },
    headStyles: { fillColor: [142, 188, 99], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
    footStyles: { fillColor: [220, 240, 200], textColor: [40, 80, 30], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 252, 245] },
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'left' },
      2: { halign: 'left' },
      3: { halign: 'center' },
      4: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'right', fontStyle: 'bold' },
      7: { halign: 'right' },
      8: { halign: 'right' },
      9: { halign: 'right' },
    },
    didParseCell: (hookData) => {
      // Colorize % Postura column (index 6) by performance
      if (hookData.section === 'body' && hookData.column.index === 6) {
        const txt = String(hookData.cell.raw || '').replace('%', '').trim();
        const pct = parseFloat(txt);
        if (!isNaN(pct)) {
          if (pct >= 90) {
            hookData.cell.styles.textColor = [120, 80, 0];
            hookData.cell.styles.fillColor = [254, 240, 138]; // amber-200
          } else if (pct >= 80) {
            hookData.cell.styles.textColor = [22, 101, 52];
          } else if (pct < 60) {
            hookData.cell.styles.textColor = [180, 30, 30];
          }
        }
      }
      // Mortalidad column (index 7): redden if > 0
      if (hookData.section === 'body' && hookData.column.index === 7) {
        const v = parseInt(String(hookData.cell.raw).replace(/,/g, ''), 10);
        if (!isNaN(v) && v > 0) hookData.cell.styles.textColor = [180, 30, 30];
      }
    },
    margin: { left: 40, right: 40 },
  });

  // ===== FOOTER =====
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const footY = doc.internal.pageSize.getHeight() - 24;
    doc.setFontSize(7.5);
    doc.setTextColor(120);
    doc.text('AVIPORK · Sistema de Gestión Avícola', 40, footY);
    doc.text(`Página ${i} de ${pageCount}`, pageW - 40, footY, { align: 'right' });
    doc.setTextColor(0);
  }

  const stamp = new Date().toISOString().split('T')[0];
  const filename = `Analisis_Produccion_${stamp}.pdf`;

  if (mode === 'print') {
    doc.autoPrint();
    const blobUrl = doc.output('bloburl');
    const win = window.open(blobUrl, '_blank');
    if (!win) {
      doc.save(filename);
      return 'saved-fallback';
    }
    return true;
  }
  doc.save(filename);
  return true;
}


// ============================================================================
// REPORTE SEMANAL POR LOTE
// ============================================================================

/**
 * Genera un PDF profesional del reporte semanal por lote.
 *
 * @param {Object} data - Respuesta de `getReporteSemanalLotes()`:
 *   { periodo: {desde, hasta}, lotes: [...], totales: {...} }
 * @param {string} mode - 'save' | 'print'
 * @returns {boolean}
 */
export function exportReporteSemanalLotesPDF(data, mode = 'print') {
  if (!data || !Array.isArray(data.lotes)) return false;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const fmtInt = (n) => Number(n || 0).toLocaleString('en-US');
  const fmtMoney = (n) =>
    `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;

  /**
   * Formatea una variación % para PDF. Devuelve string tipo "+12.3%" o "—" si null.
   * `inverso`: si true, una baja es "buena" (rotos/costos)
   */
  const fmtVar = (pct, { inverso = false, esDelta = false } = {}) => {
    if (pct === null || pct === undefined) return '—';
    const n = Number(pct);
    if (isNaN(n)) return '—';
    const signo = n > 0 ? '+' : '';
    const sufijo = esDelta ? ' pp' : '%';
    return `${signo}${n.toFixed(1)}${sufijo}`;
  };
  const colorVar = (pct, { inverso = false } = {}) => {
    if (pct === null || pct === undefined) return [130, 130, 130];
    const n = Number(pct);
    if (n === 0) return [130, 130, 130];
    const bueno = inverso ? n < 0 : n > 0;
    return bueno ? [22, 130, 82] : [200, 60, 60];
  };
  const tieneComparacion = !!data.variacion;
  const V = data.variacion || {};
  const totAnt = data.totales_anteriores || {};

  // Título y encabezado con logo
  drawAviporkHeader(doc);
  doc.setTextColor(0);
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.text('REPORTE SEMANAL POR LOTE', pageW - 40, 36, { align: 'right' });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80);
  doc.text(`Generado: ${new Date().toLocaleString('es-ES')}`, pageW - 40, 56, { align: 'right' });
  doc.setTextColor(0);

  // Franja de período
  let y = 104;
  const desdeStr = data.periodo?.desde ? formatDate(data.periodo.desde) : '—';
  const hastaStr = data.periodo?.hasta ? formatDate(data.periodo.hasta) : '—';
  const bandH = tieneComparacion ? 56 : 40;
  doc.setFillColor(245, 250, 240);
  doc.roundedRect(40, y, pageW - 80, bandH, 5, 5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(60, 100, 60);
  doc.text('PERÍODO:', 56, y + 25);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0);
  doc.text(`${desdeStr}  —  ${hastaStr}`, 130, y + 25);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(60, 100, 60);
  doc.text('LOTES CON ACTIVIDAD:', pageW - 260, y + 25);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0);
  doc.text(String(data.totales?.lotes_activos ?? data.lotes.length), pageW - 105, y + 25);

  if (tieneComparacion && data.periodo_anterior) {
    const daStr = formatDate(data.periodo_anterior.desde);
    const haStr = formatDate(data.periodo_anterior.hasta);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    doc.text('COMPARANDO CONTRA:', 56, y + 43);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(`${daStr}  —  ${haStr}`, 175, y + 43);
    doc.setTextColor(0);
  }

  y += bandH + 20;

  // KPI Cards del período (4 tarjetas) con variación si aplica
  const tot = data.totales || {};
  const cardW = (pageW - 80 - 30) / 4;
  const kpis = [
    {
      label: 'VENDIDO',
      value: fmtInt(tot.total_vendido),
      sub: `${fmtInt(tot.total_rotos || 0)} rotos`,
      color: [142, 188, 99],
      variacion: tieneComparacion ? { val: V.vendido_pct, inverso: false } : null,
    },
    {
      label: 'INGRESOS',
      value: fmtMoney(tot.total_ingresos),
      sub: `${tot.facturas_count || 0} conduces`,
      color: [16, 122, 87],
      variacion: tieneComparacion ? { val: V.ingresos_pct, inverso: false } : null,
    },
    {
      label: 'COSTOS',
      value: fmtMoney(tot.total_costos),
      sub: 'Gastos del período',
      color: [200, 90, 60],
      variacion: tieneComparacion ? { val: V.costos_pct, inverso: true } : null,
    },
    {
      label: 'UTILIDAD',
      value: fmtMoney(tot.utilidad),
      sub: `ROI: ${fmtPct(tot.roi_pct)}${tieneComparacion ? ` (${fmtVar(V.roi_delta_pp, { esDelta: true })})` : ''}`,
      color: (Number(tot.utilidad) || 0) >= 0 ? [22, 130, 82] : [200, 60, 60],
      variacion: tieneComparacion ? { val: V.utilidad_pct, inverso: false } : null,
    },
  ];
  const cardH = tieneComparacion ? 78 : 62;
  kpis.forEach((k, i) => {
    const x = 40 + i * (cardW + 10);
    doc.setFillColor(...k.color);
    doc.roundedRect(x, y, cardW, cardH, 6, 6, 'F');
    doc.setTextColor(255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(k.label, x + 12, y + 16);
    doc.setFontSize(15);
    doc.text(String(k.value), x + 12, y + 38);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(String(k.sub), x + 12, y + 54);
    if (k.variacion) {
      const txt = `vs. período anterior: ${fmtVar(k.variacion.val, { inverso: k.variacion.inverso })}`;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text(txt, x + 12, y + 70);
    }
  });
  y += cardH + 20;

  // Tabla resumen por lote
  doc.setTextColor(0);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('RESUMEN POR LOTE', 40, y);
  y += 8;

  const rows = data.lotes.map((L) => {
    const lote = L.lote || {};
    const code = lote.numero_lote || lote.codigo || lote.nombre || '—';
    const codeLbl = /^lote\b/i.test(String(code)) ? code : `Lote ${code}`;
    const Lv = L._variacion || {};
    const sinAct = !!L._sin_actividad_actual;
    const nombreCell = sinAct ? `${codeLbl}\n(sin actividad)` : codeLbl;
    // Construye una celda "valor\nvariacion" cuando hay comparación
    const withVar = (val, pct, opts = {}) => {
      if (!tieneComparacion || pct === undefined) return val;
      return `${val}\n${fmtVar(pct, opts)}`;
    };
    return [
      nombreCell,
      fmtInt(L.gallinas_actuales),
      withVar(fmtInt(L.total_producido), Lv.producido_pct),
      withVar(fmtInt(L.total_vendido), Lv.vendido_pct),
      withVar(fmtInt(L.total_rotos), Lv.rotos_pct, { inverso: true }),
      withVar(fmtMoney(L.total_ingresos), Lv.ingresos_pct),
      withVar(fmtMoney(L.total_costos), Lv.costos_pct, { inverso: true }),
      {
        content: withVar(fmtMoney(L.utilidad), Lv.utilidad_pct),
        styles: { textColor: (Number(L.utilidad) || 0) >= 0 ? [22, 130, 82] : [200, 60, 60], fontStyle: 'bold', halign: 'right' },
      },
      {
        content: withVar(fmtPct(L.roi_pct), Lv.roi_delta_pp, { esDelta: true }),
        styles: { textColor: (Number(L.roi_pct) || 0) >= 0 ? [22, 130, 82] : [200, 60, 60], fontStyle: 'bold', halign: 'right' },
      },
    ];
  });

  // Fila TOTAL con variación
  const totWithVar = (val, pct, opts = {}) => {
    if (!tieneComparacion || pct === undefined) return val;
    return `${val}\n${fmtVar(pct, opts)}`;
  };
  rows.push([
    { content: 'TOTAL', styles: { fontStyle: 'bold', fillColor: [230, 240, 220] } },
    { content: '', styles: { fillColor: [230, 240, 220] } },
    { content: totWithVar(fmtInt(tot.total_producido), V.producido_pct), styles: { fontStyle: 'bold', fillColor: [230, 240, 220], halign: 'right' } },
    { content: totWithVar(fmtInt(tot.total_vendido), V.vendido_pct), styles: { fontStyle: 'bold', fillColor: [230, 240, 220], halign: 'right' } },
    { content: totWithVar(fmtInt(tot.total_rotos), V.rotos_pct, { inverso: true }), styles: { fontStyle: 'bold', fillColor: [230, 240, 220], halign: 'right' } },
    { content: totWithVar(fmtMoney(tot.total_ingresos), V.ingresos_pct), styles: { fontStyle: 'bold', fillColor: [230, 240, 220], halign: 'right' } },
    { content: totWithVar(fmtMoney(tot.total_costos), V.costos_pct, { inverso: true }), styles: { fontStyle: 'bold', fillColor: [230, 240, 220], halign: 'right' } },
    {
      content: totWithVar(fmtMoney(tot.utilidad), V.utilidad_pct),
      styles: { fontStyle: 'bold', fillColor: [230, 240, 220], textColor: (Number(tot.utilidad) || 0) >= 0 ? [22, 130, 82] : [200, 60, 60], halign: 'right' },
    },
    {
      content: totWithVar(fmtPct(tot.roi_pct), V.roi_delta_pp, { esDelta: true }),
      styles: { fontStyle: 'bold', fillColor: [230, 240, 220], textColor: (Number(tot.roi_pct) || 0) >= 0 ? [22, 130, 82] : [200, 60, 60], halign: 'right' },
    },
  ]);

  autoTable(doc, {
    head: [['Lote', 'Gallinas', 'Producido', 'Vendido', 'Rotos', 'Ingresos', 'Costos', 'Utilidad', 'ROI']],
    body: rows,
    startY: y + 4,
    styles: { font: 'helvetica', fontSize: 9, cellPadding: 5, valign: 'middle' },
    headStyles: { fillColor: [142, 188, 99], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
    columnStyles: {
      0: { halign: 'left', fontStyle: 'bold' },
      1: { halign: 'right' },
      2: { halign: 'right' },
      3: { halign: 'right' },
      4: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'right' },
      7: { halign: 'right' },
      8: { halign: 'right' },
    },
    alternateRowStyles: { fillColor: [248, 252, 245] },
    didParseCell: (cellData) => {
      // Cuando la celda es "valor\nvariacion", pintamos la 2ª línea más chica
      // para que la variación se vea como sub-nota discreta.
      if (typeof cellData.cell.raw === 'string' && cellData.cell.raw.includes('\n')) {
        cellData.cell.styles.fontSize = 8.5;
      } else if (cellData.cell.raw && typeof cellData.cell.raw === 'object' && String(cellData.cell.raw.content || '').includes('\n')) {
        cellData.cell.styles.fontSize = 8.5;
      }
    },
  });

  // Desglose por tipo de huevo para cada lote (solo si hay ventas)
  const finalY = doc.lastAutoTable?.finalY || y + 100;
  y = finalY + 24;

  const lotesConVentas = data.lotes.filter((L) => L.total_vendido > 0);
  if (lotesConVentas.length > 0) {
    if (y > pageH - 200) {
      doc.addPage();
      y = 60;
    }
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text('DESGLOSE POR TIPO DE HUEVO Y LOTE', 40, y);
    y += 8;

    // Descubrir tipos vendidos en la semana (union)
    const tiposSet = new Set();
    lotesConVentas.forEach((L) => {
      Object.keys(L.vendido_por_tipo || {}).forEach((t) => tiposSet.add(t));
    });
    const tipos = Array.from(tiposSet).sort();

    const desgloseRows = lotesConVentas.map((L) => {
      const lote = L.lote || {};
      const code = lote.numero_lote || lote.codigo || lote.nombre || '—';
      const codeLbl = /^lote\b/i.test(String(code)) ? code : `Lote ${code}`;
      return [
        codeLbl,
        ...tipos.map((t) => fmtInt(L.vendido_por_tipo?.[t] || 0)),
        { content: fmtInt(L.total_vendido), styles: { fontStyle: 'bold' } },
        { content: fmtMoney(L.total_ingresos), styles: { fontStyle: 'bold', textColor: [22, 130, 82] } },
      ];
    });

    autoTable(doc, {
      head: [[
        'Lote',
        ...tipos.map((t) => t.charAt(0).toUpperCase() + t.slice(1)),
        'Total vendido',
        'Ingreso',
      ]],
      body: desgloseRows,
      startY: y + 4,
      styles: { font: 'helvetica', fontSize: 9, cellPadding: 5 },
      headStyles: { fillColor: [142, 188, 99], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
      columnStyles: {
        0: { halign: 'left', fontStyle: 'bold' },
      },
      alternateRowStyles: { fillColor: [248, 252, 245] },
    });
  }

  // Footer
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text('AVIPORK — Reporte generado automáticamente', 40, pageH - 20);
    doc.text(`Página ${i} de ${pages}`, pageW - 40, pageH - 20, { align: 'right' });
  }

  const filename = `reporte-semanal-lotes_${data.periodo?.desde || ''}_${data.periodo?.hasta || ''}.pdf`;
  if (mode === 'print') {
    doc.autoPrint();
    const blobUrl = doc.output('bloburl');
    const win = window.open(blobUrl, '_blank');
    if (!win) {
      doc.save(filename);
      return 'saved-fallback';
    }
    return true;
  }
  doc.save(filename);
  return true;
}


// ---------------------------------------------------------------------------
// Estado de Cuenta por Cliente (CxC) — estilo Reporte "Auxiliar Cuentas por Cobrar"
// ---------------------------------------------------------------------------

/**
 * Helper: extrae metadata multi-moneda de un pago (moneda, tasa, monto USD).
 * Compatible con backend que ya envía campos parseados o observaciones con prefijos.
 */
function _readMonedaMetaPago(pago) {
  if (!pago) return { moneda: 'DOP', tasa_cambio: null, monto_original: null };
  if (pago.moneda) {
    return {
      moneda: pago.moneda,
      tasa_cambio: pago.tasa_cambio ?? null,
      monto_original: pago.monto_original ?? null,
    };
  }
  const obs = String(pago.observaciones || '');
  const mMon = obs.match(/\[MON:([A-Z]+)\]/);
  const mTasa = obs.match(/\[TASA:([0-9.]+)\]/);
  const mUsd = obs.match(/\[USD:([0-9.]+)\]/);
  return {
    moneda: mMon ? mMon[1] : 'DOP',
    tasa_cambio: mTasa ? parseFloat(mTasa[1]) : null,
    monto_original: mUsd ? parseFloat(mUsd[1]) : null,
  };
}

function _cleanDescPago(obs) {
  if (!obs) return '';
  return String(obs)
    .replace(/\[ABONO:[^\]]+\]/g, '')
    .replace(/\[MON:[^\]]+\]/g, '')
    .replace(/\[TASA:[^\]]+\]/g, '')
    .replace(/\[USD:[^\]]+\]/g, '')
    .trim();
}

/**
 * Genera un PDF "Estado de Cuenta" para un cliente, con secciones:
 *  - Encabezado AVIPORK (verde) + metadata (fecha corte, hora, página)
 *  - Bloque de cliente (nombre, teléfono, dirección)
 *  - Tabla de FACTURAS PENDIENTES (Fecha, Nº, Descripción, Monto, Abonado, Balance)
 *  - Tabla de PAGOS APLICADOS (Fecha, Nº, Método, Moneda/Tasa, Monto DOP, Aplicado a)
 *  - Totales y firma
 *
 * @param {Object} estadoCuenta - Respuesta de /api/cxc/estado-cuenta/{cliente_id}
 * @param {Object} opts - { mode: 'save'|'print', empresa: {nombre, rnc, direccion, telefono} }
 */
export function exportEstadoCuentaToPDF(estadoCuenta, opts = {}) {
  const { mode = 'save', empresa = null } = opts;
  if (!estadoCuenta || !estadoCuenta.cliente) return false;

  const cliente = estadoCuenta.cliente;
  const facturas = estadoCuenta.facturas || [];
  const pagos = estadoCuenta.pagos || [];

  // Formato A4 apaisado (landscape) para aprovechar el ancho al mostrar
  // todas las columnas de facturas y pagos sin apretujar el texto.
  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // 1) Header
  drawAviporkHeader(doc);

  // 2) Título + metadata top-right
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(0);
  doc.text('ESTADO DE CUENTA', pageW - 40, 34, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(80);
  const hoy = new Date();
  const fechaCorte = hoy.toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const horaCorte = hoy.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' });
  doc.text(`Fecha de corte: ${fechaCorte}`, pageW - 40, 52, { align: 'right' });
  doc.text(`Hora: ${horaCorte}`, pageW - 40, 66, { align: 'right' });
  doc.text('Página 1', pageW - 40, 80, { align: 'right' });
  if (empresa?.rnc) {
    doc.text(`RNC: ${empresa.rnc}`, pageW - 40, 94, { align: 'right' });
  }
  doc.setTextColor(0);

  // 3) Bloque cliente
  const boxY = 108;
  doc.setDrawColor(200);
  doc.setLineWidth(0.5);
  doc.setFillColor(248, 252, 245);
  doc.roundedRect(40, boxY, pageW - 80, 60, 4, 4, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(60, 90, 40);
  doc.text('CLIENTE', 52, boxY + 16);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(0);
  doc.text(cliente.nombre || '—', 52, boxY + 34);
  doc.setFontSize(9);
  doc.setTextColor(80);
  const contactoBits = [];
  if (cliente.telefono) contactoBits.push(`Tel: ${cliente.telefono}`);
  if (cliente.email) contactoBits.push(`Email: ${cliente.email}`);
  if (contactoBits.length) doc.text(contactoBits.join('   ·   '), 52, boxY + 48);
  if (cliente.direccion) doc.text(`Dir: ${cliente.direccion}`, 52, boxY + 60);
  doc.setTextColor(0);

  // 4) Tabla FACTURAS PENDIENTES
  const fmtInt = (n) => Number(n || 0).toLocaleString('en-US');
  const fmtMoney = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (d) => {
    if (!d) return '—';
    const s = String(d).slice(0, 10).split('-');
    if (s.length === 3) return `${s[2]}/${s[1]}/${s[0]}`;
    return d;
  };

  // Ordenar facturas: pendientes/parciales primero por fecha ASC + numero ASC
  const facsOrdenadas = [...facturas].sort((a, b) => {
    const df = String(a.fecha || '').localeCompare(String(b.fecha || ''));
    if (df !== 0) return df;
    return String(a.numero_factura || '').localeCompare(String(b.numero_factura || ''), 'es', { numeric: true });
  });
  const pendientes = facsOrdenadas.filter((f) => ['Pendiente', 'Parcial'].includes(f.estado_pago));

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(60, 90, 40);
  doc.text('FACTURAS PENDIENTES', 40, boxY + 90);
  doc.setTextColor(0);

  autoTable(doc, {
    head: [['Fecha', 'Nº Factura', 'Descripción', 'Monto (DOP)', 'Abonado (DOP)', 'Balance (DOP)']],
    body: pendientes.map((f) => {
      const total = Number(f.total_monto || 0);
      const saldo = Number(f.saldo || 0);
      const abonado = Math.max(0, +(total - saldo).toFixed(2));
      const desc = f.observaciones || (f.items || []).map((it) => `${it.cantidad} ${it.tipo_huevo}`).join(', ') || 'Venta de huevos';
      return [
        fmtDate(f.fecha),
        f.numero_factura || '',
        desc.length > 40 ? desc.slice(0, 37) + '…' : desc,
        fmtMoney(total),
        fmtMoney(abonado),
        fmtMoney(saldo),
      ];
    }),
    startY: boxY + 100,
    styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 4 },
    headStyles: { fillColor: [142, 188, 99], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
    columnStyles: {
      0: { halign: 'center', cellWidth: 55 },
      1: { halign: 'center', cellWidth: 65, fontStyle: 'bold' },
      2: { halign: 'left' },
      3: { halign: 'right', cellWidth: 75 },
      4: { halign: 'right', cellWidth: 75 },
      5: { halign: 'right', cellWidth: 75, fontStyle: 'bold', textColor: [180, 50, 50] },
    },
    alternateRowStyles: { fillColor: [248, 252, 245] },
    foot: [[
      { content: 'TOTAL PENDIENTE (DOP)', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold' } },
      { content: fmtMoney(pendientes.reduce((s, f) => s + Number(f.total_monto || 0), 0)), styles: { halign: 'right', fontStyle: 'bold' } },
      { content: fmtMoney(pendientes.reduce((s, f) => s + Math.max(0, Number(f.total_monto || 0) - Number(f.saldo || 0)), 0)), styles: { halign: 'right', fontStyle: 'bold' } },
      { content: fmtMoney(pendientes.reduce((s, f) => s + Number(f.saldo || 0), 0)), styles: { halign: 'right', fontStyle: 'bold', textColor: [180, 50, 50] } },
    ]],
    footStyles: { fillColor: [240, 246, 232], textColor: [40, 60, 20] },
  });

  let y = doc.lastAutoTable.finalY + 20;

  // 5) Tabla PAGOS APLICADOS
  // Agrupar por abono_grupo_id para mostrar cada abono como una fila con detalle
  const pagosOrdenados = [...pagos].sort((a, b) => String(b.fecha || '').localeCompare(String(a.fecha || '')));
  const grupos = new Map();
  pagosOrdenados.forEach((p) => {
    const gid = _readMonedaMetaPago(p);
    const obs = String(p.observaciones || '');
    const m = obs.match(/\[ABONO:([^\]]+)\]/);
    const key = m ? m[1] : `single-${p.id}`;
    if (!grupos.has(key)) {
      grupos.set(key, {
        fecha: p.fecha,
        metodo: p.metodo_pago,
        moneda: gid.moneda,
        tasa: gid.tasa_cambio,
        monto_original: gid.monto_original,
        descripcion: _cleanDescPago(p.observaciones),
        items: [],
      });
    }
    grupos.get(key).items.push(p);
  });

  if (y > pageH - 200) {
    doc.addPage();
    drawAviporkHeader(doc);
    y = 108;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(60, 90, 40);
  doc.text('PAGOS APLICADOS', 40, y);
  doc.setTextColor(0);
  y += 8;

  const filasPagos = [];
  Array.from(grupos.values()).forEach((g) => {
    const totalDop = g.items.reduce((s, p) => s + Number(p.monto || 0), 0);
    const facturasAplicadas = g.items.map((p) => p.factura_numero).join(', ');
    const monedaLabel = g.moneda === 'USD' && g.tasa && g.monto_original
      ? `USD ${fmtMoney(g.monto_original)} × ${Number(g.tasa).toFixed(2)}`
      : 'DOP';
    filasPagos.push([
      fmtDate(g.fecha),
      g.metodo || '—',
      monedaLabel,
      fmtMoney(totalDop),
      facturasAplicadas,
      g.descripcion || '',
    ]);
  });

  if (filasPagos.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text('Este cliente aún no tiene pagos registrados.', 40, y + 14);
    doc.setTextColor(0);
    y += 24;
  } else {
    autoTable(doc, {
      head: [['Fecha', 'Método', 'Moneda / Tasa', 'Monto DOP', 'Facturas aplicadas', 'Descripción']],
      body: filasPagos,
      startY: y + 4,
      styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 4 },
      headStyles: { fillColor: [142, 188, 99], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
      columnStyles: {
        0: { halign: 'center', cellWidth: 55 },
        1: { halign: 'center', cellWidth: 60 },
        2: { halign: 'left', cellWidth: 100 },
        3: { halign: 'right', cellWidth: 75, fontStyle: 'bold', textColor: [40, 100, 40] },
        4: { halign: 'left' },
        5: { halign: 'left' },
      },
      alternateRowStyles: { fillColor: [248, 252, 245] },
      foot: [[
        { content: 'TOTAL ABONADO (DOP)', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold' } },
        { content: fmtMoney(filasPagos.reduce((s, r) => s + parseFloat(String(r[3]).replace(/,/g, '')), 0)), styles: { halign: 'right', fontStyle: 'bold', textColor: [40, 100, 40] } },
        { content: '', colSpan: 2 },
      ]],
      footStyles: { fillColor: [240, 246, 232], textColor: [40, 60, 20] },
    });
    y = doc.lastAutoTable.finalY + 20;
  }

  // 6) Bloque de RESUMEN
  if (y > pageH - 130) {
    doc.addPage();
    drawAviporkHeader(doc);
    y = 108;
  }
  const resumen = estadoCuenta.resumen || {};
  const resumenBoxH = 90;
  doc.setDrawColor(180);
  doc.setFillColor(240, 246, 232);
  doc.roundedRect(pageW - 260, y, 220, resumenBoxH, 4, 4, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(60, 90, 40);
  doc.text('RESUMEN DE CUENTA (DOP)', pageW - 250, y + 16);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0);
  doc.setFontSize(9);
  const rows = [
    ['Total facturado:', fmtMoney(resumen.total_facturado || 0)],
    ['Total abonado:', fmtMoney(resumen.total_pagado || 0)],
    ['Saldo pendiente:', fmtMoney(resumen.total_pendiente || 0)],
  ];
  let ry = y + 32;
  rows.forEach(([lbl, val]) => {
    doc.text(lbl, pageW - 250, ry);
    doc.text(val, pageW - 50, ry, { align: 'right' });
    ry += 14;
  });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(180, 50, 50);
  doc.text('BALANCE ACTUAL:', pageW - 250, ry + 6);
  doc.text(`$${fmtMoney(resumen.total_pendiente || 0)}`, pageW - 50, ry + 6, { align: 'right' });
  doc.setTextColor(0);

  // 7) Footer
  const footerY = pageH - 30;
  doc.setDrawColor(200);
  doc.line(40, footerY - 10, pageW - 40, footerY - 10);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text('Este estado refleja los movimientos registrados en el sistema al momento de generación.', 40, footerY);
  const nombreEmpresa = empresa?.nombre || 'AVIPORK';
  doc.text(`${nombreEmpresa} · Todos los balances en pesos dominicanos (DOP)`, pageW - 40, footerY, { align: 'right' });
  doc.setTextColor(0);

  const safeName = String(cliente.nombre || 'cliente').replace(/[^a-z0-9]+/gi, '_').slice(0, 40);
  const filename = `EstadoCuenta_${safeName}_${fechaCorte.replace(/\//g, '-')}.pdf`;

  if (mode === 'print') {
    try {
      const blob = doc.output('blob');
      const url = URL.createObjectURL(blob);
      const w = window.open(url);
      if (w) {
        w.onload = () => {
          try { w.print(); } catch (_e) { /* noop */ }
        };
      }
    } catch (_e) { /* noop */ }
    return true;
  }
  doc.save(filename);
  return true;
}
